import { useRef, useState, type ReactNode } from "react";
import { CalendarClock, CalendarPlus } from "lucide-react";
import { ApiError } from "@/core/api/apiTypes";
import { Button } from "@/shared/components/shadcn/Button";
import { Input } from "@/shared/components/shadcn/Input";
import { Select } from "@/shared/components/shadcn/Select";
import { Switch } from "@/shared/components/shadcn/Switch";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FieldGroup } from "@/shared/components/ui/FieldGroup";
import { FormField } from "@/shared/components/ui/FormField";
import { FormPage } from "@/shared/components/ui/FormPage";
import { SectionCard } from "@/shared/components/ui/SectionCard";
import { useUpdateAppointmentRules } from "../hooks/appointmentRuleQueries";
import { AppointmentNoticeSchema, type AppointmentRules } from "../types/appointmentRuleTypes";

type NoticeInput = { amount: string; unit: number };
type NoticeKey = "cancellation" | "rescheduling" | "booking";
const noticeLabels: Record<NoticeKey, string> = {
  cancellation: "Cancel until", rescheduling: "Reschedule until", booking: "Minimum booking notice",
};

function noticeInput(minutes: number): NoticeInput {
  const unit = minutes === 0 ? 1 : minutes % 1440 === 0 ? 1440 : minutes % 60 === 0 ? 60 : 1;
  return { amount: String(minutes / unit), unit };
}

function noticeInputs(rules: AppointmentRules): Record<NoticeKey, NoticeInput> {
  return {
    cancellation: noticeInput(rules.cancellationNoticeMinutes),
    rescheduling: noticeInput(rules.reschedulingNoticeMinutes),
    booking: noticeInput(rules.minimumBookingNoticeMinutes),
  };
}

function NoticeField({ name, value, error, onChange }: {
  name: NoticeKey; value: NoticeInput; error?: string; onChange: (value: NoticeInput) => void;
}) {
  const label = noticeLabels[name];
  return (
    <fieldset className="min-w-0 space-y-[7px]">
      <legend className="text-sm font-semibold text-foreground">{label}</legend>
      <div className="grid grid-cols-2 items-start gap-3">
        <FormField id={`${name}-notice`} label={label} error={error} className="space-y-0 [&>label]:sr-only">
          <Input type="number" min={0} max={Math.floor(2147483647 / value.unit)} step={1} value={value.amount}
            onChange={(event) => onChange({ ...value, amount: event.target.value })} />
        </FormField>
        <FormField id={`${name}-unit`} label={`${label} unit`} className="space-y-0 [&>label]:sr-only">
          <Select value={value.unit} onChange={(event) => onChange({ ...value, unit: Number(event.target.value) })}>
            <option value={1}>Minutes</option><option value={60}>Hours</option><option value={1440}>Days</option>
          </Select>
        </FormField>
      </div>
      {name !== "booking" && <p className="text-xs text-muted-foreground">Before the appointment.</p>}
    </fieldset>
  );
}

interface AppointmentRulesSectionProps {
  header: ReactNode;
  businessId: string;
  rules: AppointmentRules;
  refreshError?: string;
  onRetry: () => void;
  onReload: () => Promise<AppointmentRules>;
}

export function AppointmentRulesSection({ header, businessId, rules, refreshError, onRetry, onReload }: AppointmentRulesSectionProps) {
  const [draft, setDraft] = useState(rules);
  const [notices, setNotices] = useState(() => noticeInputs(rules));
  const [errors, setErrors] = useState<Partial<Record<NoticeKey, string>>>({});
  const [saveError, setSaveError] = useState("");
  const [notice, setNotice] = useState("");
  const [conflict, setConflict] = useState(false);
  const [reloadOpen, setReloadOpen] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [reloadError, setReloadError] = useState("");
  const submitting = useRef(false);
  const update = useUpdateAppointmentRules(businessId);
  const busy = update.isPending || reloading;
  const hasConflict = conflict || rules.revision !== draft.revision;

  function clearFeedback() {
    setSaveError("");
    setNotice("");
    setErrors({});
  }

  async function save(form: HTMLFormElement) {
    if (submitting.current || hasConflict) return;
    clearFeedback();
    const invalid: Partial<Record<NoticeKey, string>> = {};
    for (const name of ["cancellation", "rescheduling", "booking"] as const) {
      const value = notices[name];
      const amount = Number(value.amount);
      if (!value.amount.trim() || !Number.isInteger(amount) ||
        !AppointmentNoticeSchema.safeParse(amount * value.unit).success) {
        invalid[name] = "Enter a whole amount from 0 to 2,147,483,647 minutes in total.";
      }
    }
    if (Object.keys(invalid).length) {
      setErrors(invalid);
      requestAnimationFrame(() => form.querySelector<HTMLInputElement>('[aria-invalid="true"]')?.focus());
      return;
    }
    submitting.current = true;
    try {
      const saved = await update.mutateAsync({
        expectedRevision: draft.revision,
        customerCancellationEnabled: draft.customerCancellationEnabled,
        cancellationNoticeMinutes: Number(notices.cancellation.amount) * notices.cancellation.unit,
        customerReschedulingEnabled: draft.customerReschedulingEnabled,
        reschedulingNoticeMinutes: Number(notices.rescheduling.amount) * notices.rescheduling.unit,
        minimumBookingNoticeMinutes: Number(notices.booking.amount) * notices.booking.unit,
      });
      setDraft(saved);
      setNotices(noticeInputs(saved));
      setNotice("Appointment rules saved.");
    } catch (error) {
      if (error instanceof ApiError && error.Status === 409) setConflict(true);
      else setSaveError(error instanceof Error ? error.message : "Couldn't save appointment rules. Try again.");
    } finally {
      submitting.current = false;
    }
  }

  async function reload() {
    if (submitting.current) return;
    submitting.current = true;
    setReloading(true);
    setReloadError("");
    try {
      const saved = await onReload();
      setDraft(saved);
      setNotices(noticeInputs(saved));
      setConflict(false);
      clearFeedback();
      setReloadOpen(false);
      setNotice("Saved appointment rules loaded.");
    } catch (error) {
      setReloadError(error instanceof Error ? error.message : "Couldn't reload appointment rules. Your draft has been kept.");
    } finally {
      submitting.current = false;
      setReloading(false);
    }
  }

  return (
    <>
      <FormPage
        header={header}
        contentClassName="space-y-6"
        onSubmit={(event) => { event.preventDefault(); void save(event.currentTarget); }}
        actions={<Button type="submit" formNoValidate disabled={busy || hasConflict}>{update.isPending ? "Saving..." : "Save appointment rules"}</Button>}
      >
        {refreshError && <ErrorState title="Couldn't refresh appointment rules" message={`${refreshError} Your draft is unchanged.`} onRetry={onRetry} />}
        {hasConflict && <div role="alert" className="space-y-3">
          <p className="text-sm text-muted-foreground">Appointment rules changed elsewhere or are busy. Your draft has been kept. Reload the saved rules before saving again.</p>
          <Button variant="outline" disabled={busy} onClick={() => { setReloadError(""); setReloadOpen(true); }}>Reload saved rules</Button>
        </div>}
        <SectionCard
          title="New appointments"
          icon={<CalendarPlus />}
          description="Set how much notice you need before a new booking."
        >
          <FieldGroup disabled={busy} className="rounded-none border-0 bg-transparent p-0 sm:p-0">
            <NoticeField name="booking" value={notices.booking} error={errors.booking}
              onChange={(value) => { clearFeedback(); setNotices((previous) => ({ ...previous, booking: value })); }} />
          </FieldGroup>
        </SectionCard>
        <SectionCard
          title="Customer appointment management"
          icon={<CalendarClock />}
          description="Choose how customers can cancel or reschedule their appointments."
        >
          <FieldGroup disabled={busy} className="rounded-none border-0 bg-transparent p-0 sm:p-0">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 space-y-1">
                <label htmlFor="cancellation-enabled" className="text-sm font-semibold">Allow customer cancellation</label>
              </div>
              <Switch id="cancellation-enabled" size="lg"
                checked={draft.customerCancellationEnabled} onCheckedChange={(enabled) => {
                  clearFeedback(); setDraft((previous) => ({ ...previous, customerCancellationEnabled: enabled }));
                }} />
            </div>
            <NoticeField name="cancellation" value={notices.cancellation} error={errors.cancellation}
              onChange={(value) => { clearFeedback(); setNotices((previous) => ({ ...previous, cancellation: value })); }} />
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 space-y-1">
                <label htmlFor="rescheduling-enabled" className="text-sm font-semibold">Allow customer rescheduling</label>
              </div>
              <Switch id="rescheduling-enabled" size="lg"
                checked={draft.customerReschedulingEnabled} onCheckedChange={(enabled) => {
                  clearFeedback(); setDraft((previous) => ({ ...previous, customerReschedulingEnabled: enabled }));
                }} />
            </div>
            <NoticeField name="rescheduling" value={notices.rescheduling} error={errors.rescheduling}
              onChange={(value) => { clearFeedback(); setNotices((previous) => ({ ...previous, rescheduling: value })); }} />
          </FieldGroup>
          <p className="text-xs leading-relaxed text-muted-foreground">Cancellation and rescheduling rule changes apply only to new appointments.</p>
        </SectionCard>
        {saveError && <ErrorState title="Couldn't save appointment rules" message={`${saveError} Your draft has been kept.`} />}
        <p role="status" aria-live="polite" className="text-sm text-primary">{notice}</p>
      </FormPage>
      <ConfirmationDialog open={reloadOpen} onOpenChange={setReloadOpen} title="Reload saved appointment rules?"
        description="This replaces your unsaved draft with the latest rules. If loading fails, your draft will be kept."
        confirmLabel="Reload rules" dismissLabel="Keep reviewing" pending={reloading} error={reloadError} onConfirm={() => void reload()} />
    </>
  );
}
